import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Archive, ArrowRight, CheckCircle2, CircleSlash } from 'lucide-react';
import { Button, Card, CardContent } from '@command-center/ui-system';
import { leadloopApi, type CascadeContact, type IntakeItem } from '../lib/leadloop-api';

export interface IntakeSourceState {
  intake?: IntakeItem;
  lead?: { id: string; relationship: string };
  contacts: CascadeContact[];
  contactsLoading: boolean;
}

export function useIntakeSourceState(
  sourceType: IntakeItem['sourceType'],
  sourceId?: string,
  fallbackLeadId?: string,
): IntakeSourceState {
  const source = useQuery({
    queryKey: ['intake-source', sourceType, sourceId],
    queryFn: () => leadloopApi.intakeSource(sourceType, sourceId!),
    enabled: Boolean(sourceId),
  });

  return {
    intake: source.data?.intake ?? undefined,
    lead: source.data?.lead
      ?? (fallbackLeadId ? { id: fallbackLeadId, relationship: 'linked_existing' } : undefined),
    contacts: source.data?.contacts ?? [],
    contactsLoading: source.isLoading,
  };
}

/*
  `useIntakeReviewActions` used to live here: Link to Lead / Dismiss / Create Lead,
  rendered on every unresolved card in the LeadLoop queue. It was removed with the
  intake tab on 2026-08-17, when legitimate intake started becoming a Lead
  automatically — the queue holds Leads now, and a queue of records asking to be
  retyped as Leads is the thing that change was for.

  The same three actions still exist, on the Call or Form Entry itself, in
  `IntakeActionCenter`. They are the override for a record the conservative
  obvious-garbage filter suppressed and a human disagrees with, which is a different
  job from being the only way in.
*/

export function IntakeRecordStatusBanner({
  sourceType,
  state,
  hideResolvedLead = false,
}: {
  sourceType: IntakeItem['sourceType'];
  state: IntakeSourceState;
  /** The host may move the resolved Lead action into the record header. */
  hideResolvedLead?: boolean;
}) {
  const noun = sourceType === 'call' ? 'Call' : 'Entry';

  if (state.lead) {
    if (hideResolvedLead) return null;
    const message = state.lead.relationship === 'created'
      ? `This ${noun} was turned into a Lead.`
      : `This ${noun} is linked to a Lead.`;
    return (
      <Card className="border-success/30 bg-success/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden />
            <p className="text-sm font-semibold text-foreground">{message}</p>
          </div>
          <Button type="button" size="sm" variant="outline" asChild>
            <Link to={`/leads/${encodeURIComponent(state.lead.id)}`}>
              Open Lead
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (state.intake && /^(unresolved|claiming)$/.test(state.intake.status)) {
    /*
      An unresolved, still-eligible intake used to get its own "Action needed"
      card holding Link / Dismiss / Create Lead. Those three buttons now live in
      the Action Center directly below this banner, beside the note box, so the
      record has one place that acts on it instead of two stacked cards both
      claiming to be where the action is.
    */
    if (sourceType === 'call' && state.intake.leadEligibility === 'not_eligible') {
      // The eligibility toggle immediately above already states this outcome.
      // Repeating it in a separate card made the header area look like two
      // competing action centers. Archive now lives in the call header.
      return null;
    }
    return null;
  }

  /*
    Dismissed. Worth a banner where the queue card is not: on the record itself
    the reader has arrived deliberately — from a search, a contact's history, a
    link — and needs to know why this one has no actions on it. The Action
    Center below hides Create Lead / Link Lead / Dismiss once a decision exists,
    and a record with a note box and nothing else is a puzzle without this line.
  */
  if (state.intake?.lifecycle === 'dismissed') {
    return (
      <Card className="border-border bg-muted/30">
        <CardContent className="flex flex-wrap items-center gap-2.5 p-4">
          <CircleSlash className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-sm font-semibold text-foreground">
            This {noun} was dismissed.
          </p>
          <p className="text-sm text-muted-foreground">Nothing was deleted.</p>
        </CardContent>
      </Card>
    );
  }

  return null;
}

/**
 * Compact resolved-state action for record headers. Form entries use this in
 * place of the full-width success banner so the top of the record stays quiet.
 */
export function IntakeLeadHeaderAction({
  lead,
}: {
  lead?: IntakeSourceState['lead'];
}) {
  if (!lead) return null;
  return (
    <Button
      type="button"
      size="sm"
      asChild
      className="border border-success bg-success text-success-foreground hover:bg-success/90"
    >
      <Link to={`/leads/${encodeURIComponent(lead.id)}`}>
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        Open Lead
      </Link>
    </Button>
  );
}

/**
 * Call lifecycle action placed beside the call's title and badges. It remains
 * available after archiving so the same control can restore the record.
 */
export function IntakeArchiveHeaderAction({
  archived,
  onArchive,
}: {
  archived?: boolean;
  onArchive: (archive: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const run = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await onArchive(!archived);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={run}>
        <Archive className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        {archived ? 'Restore Call' : 'Archive Call'}
      </Button>
      {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
    </div>
  );
}
